import { useState, useEffect, useCallback } from "react";
import { queryGetServices } from "src/graphql";
import { IService, IServiceFilters, ServiceOrder } from "src/domains";
import {
  BlockUserEvent,
  UnblockUserEvent,
  CreateReviewEvent,
  OfferUpdateEvent,
  TabChangeEvent,
} from "src/utils/event";
import { EventRegister } from "react-native-event-listeners";
import { AppState, AppStateStatus } from "react-native";
import Queue from "queue-promise";
import { useRecoilValue } from "recoil";
import { useAuth, AuthState } from "src/hooks/cognito";

const queueSearchRequests = new Queue({
  concurrent: 1,
});

export const useRecommendations = () => {
  const auth = useAuth();
  const [items, setItems] = useState<IService[]>([]);
  const [totalCount, setTotalCount] = useState<number | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();

  // FIXME: Add auth change reload
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const data = await queryGetServices(
        {
          filter: {
            isBlocked: false,
            isDisabled: false,
          },
          order: ServiceOrder.SERVICE_RECOMMENDATION,
          limit: 10,
        },
        auth === AuthState.Auth ? "AMAZON_COGNITO_USER_POOLS" : "API_KEY"
      );
      setItems(data?.items || []);
      setTotalCount(data?.totalCount);
    } catch (e) {
      console.error(e);
      setError(e as Error);
    }
    setLoading(false);
  }, [auth]);

  const nextPage = useCallback(async () => {
    try {
      if (items.length > 0 && totalCount && totalCount > items.length) {
        const data = await queryGetServices(
          {
            filter: {
              isBlocked: false,
              isDisabled: false,
            },
            order: ServiceOrder.SERVICE_RECOMMENDATION,
            limit: 10,
            offset: items.length,
          },
          auth === AuthState.Auth ? "AMAZON_COGNITO_USER_POOLS" : "API_KEY"
        );
        setItems((v) => [
          ...v,
          ...(data?.items?.filter(
            (item) => v.findIndex((a) => a.id === item.id) === -1
          ) || []),
        ]);
      }
    } catch (e) {
      console.error(e);
    }
  }, [totalCount, items, auth]);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange]);

  useEffect(() => {
    const blockUserEventListener = EventRegister.addEventListener(
      BlockUserEvent,
      () => refresh()
    );
    const unblockUserEventListener = EventRegister.addEventListener(
      UnblockUserEvent,
      () => refresh()
    );
    const createReviewListener = EventRegister.addEventListener(
      CreateReviewEvent,
      () => refresh()
    );
    const offerUpdateListener = EventRegister.addEventListener(
      OfferUpdateEvent,
      () => refresh()
    );
    const tabChangeListener = EventRegister.addEventListener(
      TabChangeEvent,
      (e) => {
        if (e.tabName === "Home") {
          refresh();
        }
      }
    );

    return () => {
      EventRegister.removeEventListener(blockUserEventListener as string);
      EventRegister.removeEventListener(unblockUserEventListener as string);
      EventRegister.removeEventListener(createReviewListener as string);
      EventRegister.removeEventListener(offerUpdateListener as string);
      EventRegister.removeEventListener(tabChangeListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return [items, totalCount, loading, error, refresh, nextPage] as const;
};

export const useSearch = (order: ServiceOrder, filter?: IServiceFilters) => {
  const auth = useAuth();
  const [items, setItems] = useState<IService[]>([]);
  const [totalCount, setTotalCount] = useState<number | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();

  // FIXME: Add auth change reload
  const refresh = useCallback(async () => {
    queueSearchRequests.enqueue(() => {
      return new Promise(async function (resolve) {
        if (!filter) {
          setItems([]);
          resolve(true);
          return;
        }
        setLoading(true);
        setError(undefined);
        try {
          const data = await queryGetServices(
            {
              filter: {
                ...(filter || {}),
                isBlocked: false,
                isDisabled: false,
              },
              order,
              limit: 10,
              offset: 0,
            },
            auth === AuthState.Auth ? "AMAZON_COGNITO_USER_POOLS" : "API_KEY"
          );
          setItems(data?.items || []);
          setTotalCount(data?.totalCount);
        } catch (e) {
          console.log("e", e);
          setError(e as Error);
        }
        setLoading(false);
        resolve(true);
      });
    });
  }, [filter, order, auth]);

  const nextPage = useCallback(async () => {
    try {
      if (items.length > 0 && totalCount && totalCount > items.length) {
        const data = await queryGetServices(
          {
            filter: {
              ...(filter || {}),
              isBlocked: false,
              isDisabled: false,
            },
            order,
            limit: 10,
            offset: items.length,
          },
          auth === AuthState.Auth ? "AMAZON_COGNITO_USER_POOLS" : "API_KEY"
        );
        setItems((v) => [
          ...v,
          ...(data?.items?.filter(
            (item) => v.findIndex((a) => a.id === item.id) === -1
          ) || []),
        ]);
      }
    } catch (e) {
      console.error(e);
    }
  }, [totalCount, items, auth]);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange]);

  useEffect(() => {
    const blockUserEventListener = EventRegister.addEventListener(
      BlockUserEvent,
      () => refresh()
    );
    const unblockUserEventListener = EventRegister.addEventListener(
      UnblockUserEvent,
      () => refresh()
    );
    const createReviewListener = EventRegister.addEventListener(
      CreateReviewEvent,
      () => refresh()
    );
    const offerUpdateListener = EventRegister.addEventListener(
      OfferUpdateEvent,
      () => refresh()
    );
    const tabChangeListener = EventRegister.addEventListener(
      TabChangeEvent,
      (e) => {
        if (e.tabName === "Home") {
          refresh();
        }
      }
    );
    return () => {
      EventRegister.removeEventListener(blockUserEventListener as string);
      EventRegister.removeEventListener(unblockUserEventListener as string);
      EventRegister.removeEventListener(createReviewListener as string);
      EventRegister.removeEventListener(offerUpdateListener as string);
      EventRegister.removeEventListener(tabChangeListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [filter, order]);

  return [
    items,
    totalCount,
    loading || queueSearchRequests.size > 0,
    error,
    refresh,
    nextPage,
  ] as const;
};
